import React, { useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import { RbacV2Banner } from '../RbacV2Banner';
import { AlertHost, AlertHostProvider } from '../patterns';
import './AppShell.css';

const PIN_STORAGE_KEY = 'shell.sidebar.pinned';

function readPinned(): boolean {
  try {
    return localStorage.getItem(PIN_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

export interface AppShellProps {
  children: ReactNode;
}

const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const { t } = useTranslation();
  const isPhone = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pinned, setPinned] = useState<boolean>(readPinned);
  const drawerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  const togglePin = useCallback(() => {
    setPinned(p => {
      const next = !p;
      try {
        localStorage.setItem(PIN_STORAGE_KEY, String(next));
      } catch { /* ignore quota errors */ }
      return next;
    });
  }, []);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // On Escape, close the drawer.
  useEffect(() => {
    if (!drawerOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeDrawer();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen, closeDrawer]);

  // Focus management: move focus into drawer on open, restore on close.
  useEffect(() => {
    if (drawerOpen) {
      triggerRef.current = document.activeElement as HTMLElement | null;
      const firstFocusable = drawerRef.current?.querySelector<HTMLElement>(
        'a, button, [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();
    } else if (triggerRef.current) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  }, [drawerOpen]);

  // Sidebar rendering rules:
  //  - desktop:    full sidebar inline.
  //  - tablet, !pinned: 56px icon-rail.
  //  - tablet, pinned:  full sidebar inline.
  //  - phone:      sidebar hidden inline, drawer renders only when open.
  const sidebarMode: 'full' | 'collapsed' | 'hidden' = isPhone
    ? 'hidden'
    : isTablet && !pinned
      ? 'collapsed'
      : 'full';

  return (
    <AlertHostProvider>
      <div className={`shell shell--${sidebarMode}`}>
        <a href="#main-content" className="shell__skip-link">
          {t('shell.skipToContent')}
        </a>
        {sidebarMode !== 'hidden' && (
          <Sidebar onCloseMobile={closeDrawer} collapsed={sidebarMode === 'collapsed'} />
        )}
        <div className="shell__main">
          <TopBar onOpenMobile={() => setDrawerOpen(true)} onTogglePin={togglePin} pinned={pinned} />
          <RbacV2Banner />
          <main id="main-content" className="shell__content">{children}</main>
        </div>
        {isPhone && drawerOpen && (
          <>
            <div className="shell__backdrop" onClick={closeDrawer} aria-hidden="true" />
            <div className="shell__drawer" data-testid="shell-drawer" role="dialog" aria-modal="true" aria-label={t('shell.sidebarLabel')} ref={drawerRef}>
              <Sidebar onCloseMobile={closeDrawer} />
            </div>
          </>
        )}
      </div>
      <AlertHost />
    </AlertHostProvider>
  );
};

export default AppShell;
